"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";

export type SelectItem = {
  value: string;
  label: string;
  disabled?: boolean;
};

type SelectProps = {
  value: string;
  items: SelectItem[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  onChange: (value: string) => void;
  /** Optional: force menu to open upwards when near bottom. */
  preferUpward?: boolean;
  /** Optional: max height for option list. */
  maxMenuHeightPx?: number;
  className?: string;
};

/**
 * Common Select (ECQ style).
 *
 * Why: consistent UI + better control for custom styling than native <select>.
 * Implementation note: uses a button+listbox pattern and is controlled by `value`.
 */
export default function Select({
  value,
  items,
  placeholder = "Please select",
  disabled,
  error,
  onChange,
  preferUpward,
  maxMenuHeightPx = 280,
  className,
}: SelectProps) {
  const reactId = useId();
  const buttonId = `select-${reactId}`;
  const listboxId = `listbox-${reactId}`;
  const errorId = `select-error-${reactId}`;

  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Trigger row only — the menu is positioned off this, not off the root (which also holds the error text). */
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [isClient, setIsClient] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const [portalPos, setPortalPos] = useState<null | {
    left: number;
    width: number;
    top?: number;
    bottom?: number;
  }>(null);

  const selected = useMemo(() => items.find((i) => i.value === value), [items, value]);

  // Show a search box for long lists (e.g. the 200+ country list).
  const searchable = items.length > 8;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
  }, [items, query]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Reset the search query whenever the menu closes.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!open) return;
      const el = rootRef.current;
      if (!el) return;
      if (!(e.target instanceof Node)) return;

      // If the click is inside the trigger OR inside the portaled menu, keep it open.
      if (el.contains(e.target)) return;
      const portalEl = portalRef.current;
      if (portalEl?.contains(e.target)) return;

      setOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = triggerRef.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      const shouldOpenUp = rect.bottom > window.innerHeight * 0.6;
      const nextOpenUp = Boolean(preferUpward) || shouldOpenUp;
      setOpenUpward(nextOpenUp);

      const gapPx = 8;
      // Use `position: fixed` portal to escape stacking contexts / overflow clipping in parent layouts.
      setPortalPos(
        nextOpenUp
          ? {
              left: rect.left,
              width: rect.width,
              bottom: window.innerHeight - rect.top + gapPx,
            }
          : {
              left: rect.left,
              width: rect.width,
              top: rect.bottom + gapPx,
            }
      );
    };

    compute();
    window.addEventListener("resize", compute);
    // Capture scroll from nested containers too.
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, preferUpward]);

  const triggerBorder = error
    ? "border-b border-primary-600"
    : open || selected
      ? "border-b-[1.5px] border-secondary-500"
      : "border-b border-neutral-500";

  const triggerText = selected ? "text-white" : "text-neutral-500";

  return (
    <div
      ref={rootRef}
      className={clsx(
        "relative w-full",
        // Ensure the open select stacks above subsequent fields (avoids "transparent/un-clickable" overlay issues).
        open && "z-[10000]",
        className
      )}
    >
      <div ref={triggerRef} className="relative w-full">
        <button
          id={buttonId}
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
          className={clsx(
            // mx-4 (not px-4) for the same reason as TextField: the underline is on
            // this element, so it has to be inset with the 16px text grid.
            "w-[calc(100%-32px)] mx-4 h-10 flex items-center bg-transparent font-[var(--font-avenir)] font-normal text-[16px] leading-7 tracking-[0.5px] focus:outline-none transition-colors cursor-pointer",
            triggerBorder,
            disabled && "cursor-not-allowed opacity-70",
            triggerText
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-describedby={error ? errorId : undefined}
        >
          <span className="block w-full text-left pr-10 truncate">
            {selected ? selected.label : placeholder}
          </span>
        </button>

        {/* Chevron only — the error message sits under the field (see below). */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={clsx("transition-transform duration-200", open && "rotate-180")}
          >
            <path
              d="M18 9.00005C18 9.00005 13.5811 15 12 15C10.4188 15 6 9 6 9"
              stroke="var(--color-neutral-300)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Helper line below the underline, same slot/styling as TextField's error. */}
      {error ? (
        <p
          id={errorId}
          className="px-4 mt-2 text-primary-600 text-[12px] leading-5 tracking-[0.25px] font-[var(--font-avenir)]"
        >
          {error}
        </p>
      ) : null}

      {/* Options (portaled) */}
      {isClient && open && portalPos
        ? createPortal(
            <div
              ref={portalRef}
              className={clsx(
                "fixed z-[2147483647]",
                // Keep menu visually aligned with open direction.
                openUpward ? "origin-bottom" : "origin-top"
              )}
              style={{
                left: portalPos.left,
                width: portalPos.width,
                top: portalPos.top,
                bottom: portalPos.bottom,
              }}
            >
              <div className="rounded-[4px] bg-surface-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)] overflow-hidden transition-all duration-200 opacity-100">
                {searchable && (
                  <div className="px-4 pt-3 pb-2 border-b border-white/10">
                    <input
                      autoFocus
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full bg-transparent outline-none text-[16px] leading-7 tracking-[0.5px] text-white placeholder:text-neutral-500 font-[var(--font-avenir)]"
                    />
                  </div>
                )}
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-labelledby={buttonId}
                  className="ecq-scrollbar py-2 overflow-auto"
                  style={{ maxHeight: `${maxMenuHeightPx}px` }}
                >
                  {filtered.length === 0 ? (
                    <li className="px-6 py-2 text-[16px] leading-7 tracking-[0.5px] text-neutral-400">
                      No results
                    </li>
                  ) : (
                    filtered.map((item) => {
                      const isSelected = item.value === value;
                      return (
                        <li key={item.value} role="option" aria-selected={isSelected}>
                          <button
                            type="button"
                            disabled={item.disabled}
                            onClick={() => {
                              if (item.disabled) return;
                              onChange(item.value);
                              setOpen(false);
                            }}
                            className={clsx(
                              // Figma "Select-Cell": default surface-3, hover surface-2, selected neutral-500.
                              "w-full text-left px-6 py-2 text-[16px] leading-7 tracking-[0.5px] transition-colors text-white",
                              item.disabled
                                ? "text-white/30 cursor-not-allowed"
                                : "hover:bg-surface-2",
                              isSelected && "bg-neutral-500"
                            )}
                          >
                            {item.label}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
